import {Controller} from 'stimulus';
import ImageResizer from '../helpers/image_resizer';

/**
 * Dropzone Inline Controller
 *
 * Provides drag-drop file upload UI with client-side image resizing,
 * but stores the file in a regular file input for form submission
 * instead of uploading immediately.
 *
 * Use this for forms that submit to Rails2 or other backends that
 * expect multipart form data with file fields.
 */
export default class extends Controller {
  static targets = ['fileInput', 'preview', 'placeholder', 'spinner'];

  connect() {
    this.abortController = new AbortController();
    this.setupDragAndDrop();
    this.setupFileInput();
  }

  disconnect() {
    if (this.abortController) this.abortController.abort();
  }

  setupDragAndDrop() {
    const opts = {signal: this.abortController.signal};

    this.element.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.element.classList.add('dz-drag-hover');
    }, opts);

    this.element.addEventListener('dragleave', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.element.classList.remove('dz-drag-hover');
    }, opts);

    this.element.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.element.classList.remove('dz-drag-hover');

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.handleFile(files[0]);
      }
    }, opts);

    // Click to select file
    this.element.addEventListener('click', (e) => {
      // Don't trigger if clicking the file input itself
      if (e.target !== this.fileInputTarget) {
        this.fileInputTarget.click();
      }
    }, opts);
  }

  setupFileInput() {
    this.fileInputTarget.addEventListener('change', () => {
      if (this.fileInputTarget.files.length > 0) {
        this.handleFile(this.fileInputTarget.files[0]);
      }
    }, {signal: this.abortController.signal});
  }

  async handleFile(file) {
    // Validate file type
    const acceptedTypes = this.fileInputTarget.accept;
    if (acceptedTypes && !this.isFileTypeAccepted(file, acceptedTypes)) {
      this.showError(this.data.get('errorFormat') || 'Invalid file type');
      return;
    }

    // Validate file size (before resize)
    const maxSize = parseInt(this.data.get('maxFilesize') || '50', 10) * 1024 * 1024;
    if (file.size > maxSize) {
      this.showError(this.data.get('errorMaxSize') || 'File too large');
      return;
    }

    const resizeEnabled = this.data.get('resizeEnabled') === 'true';

    if (resizeEnabled) {
      this.showSpinner(true);

      try {
        const processedFile = await ImageResizer.processFile(file, {
          onStart: () => this.showResizingState(true),
          onComplete: () => this.showResizingState(false),
        });

        // Update the file input with the processed file
        this.updateFileInput(processedFile);

        // Show preview
        this.showPreview(processedFile);
      } catch (error) {
        console.error('Error processing file:', error);
        // Fall back to original file
        this.updateFileInput(file);
        this.showPreview(file);
      } finally {
        this.showSpinner(false);
      }
    } else {
      // No resize - use original file
      this.updateFileInput(file);
      this.showPreview(file);
    }
  }

  isFileTypeAccepted(file, acceptedTypes) {
    const types = acceptedTypes.split(',').map((t) => t.trim().toLowerCase());
    const fileType = file.type.toLowerCase();
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

    return types.some((type) => {
      if (type.startsWith('.')) {
        return type === fileExtension;
      }
      if (type.endsWith('/*')) {
        return fileType.startsWith(type.replace('/*', '/'));
      }
      return type === fileType;
    });
  }

  updateFileInput(file) {
    // Use DataTransfer to set the file on the input
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    this.fileInputTarget.files = dataTransfer.files;
  }

  showPreview(file) {
    if (!this.hasPreviewTarget) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      this.previewTarget.src = e.target.result;
      this.previewTarget.classList.remove('d-none');
      if (this.hasPlaceholderTarget) {
        this.placeholderTarget.classList.add('d-none');
      }
    };
    reader.readAsDataURL(file);
  }

  showSpinner(visible) {
    if (!this.hasSpinnerTarget) return;

    if (visible) {
      this.spinnerTarget.classList.remove('d-none');
    } else {
      this.spinnerTarget.classList.add('d-none');
    }
  }

  showResizingState(visible) {
    if (visible) {
      this.element.classList.add('resizing');
    } else {
      this.element.classList.remove('resizing');
    }
  }

  showError(message) {
    // Use toast if available, otherwise alert
    if (typeof showToast === 'function') {
      showToast(message, 'alert alert-danger');
    } else {
      alert(message);
    }
  }

  // Action to clear the selected file
  clear(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    this.fileInputTarget.value = '';

    if (this.hasPreviewTarget) {
      this.previewTarget.src = '';
      this.previewTarget.classList.add('d-none');
    }

    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.remove('d-none');
    }
  }
}
